import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { TypographyH2, TypographyP, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { getSupportedLocaleOrDefault, isCjkLocale } from '@heidi/utils/i18n';

interface NameStepProps {
  firstName: string;
  lastName: string;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onContinue: () => void;
  isValid: boolean;
}

export function NameStep({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  onContinue,
  isValid,
}: NameStepProps) {
  const { t, locale } = useTranslations();
  const isCjk = isCjkLocale(getSupportedLocaleOrDefault(locale));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onContinue();
    }
  };

  const firstNameField = (
    <div key="firstName" className="flex flex-1 flex-col gap-1.5">
      <TypographyP2>{t('signup.firstName')}</TypographyP2>
      <Input
        id="firstName"
        data-testid="personalization-first-name"
        value={firstName}
        onChange={(e) => onFirstNameChange(e.target.value)}
        placeholder={t('signup.firstNamePlaceholder')}
        autoComplete="given-name"
        autoFocus={!isCjk}
      />
    </div>
  );

  const lastNameField = (
    <div key="lastName" className="flex flex-1 flex-col gap-1.5">
      <TypographyP2>{t('signup.lastName')}</TypographyP2>
      <Input
        id="lastName"
        data-testid="personalization-last-name"
        value={lastName}
        onChange={(e) => onLastNameChange(e.target.value)}
        placeholder={t('signup.lastNamePlaceholder')}
        autoComplete="family-name"
        autoFocus={isCjk}
      />
    </div>
  );

  const nameFields = isCjk
    ? [lastNameField, firstNameField]
    : [firstNameField, lastNameField];

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="personalization-name-step"
      className="flex h-full flex-col gap-6 lg:gap-10"
    >
      {/* Header - different copy on mobile vs desktop */}
      <div className="flex flex-col gap-4">
        <TypographyH2 className="lg:hidden">{t('signup.letsPersonalise')}</TypographyH2>
        <TypographyH2 className="hidden lg:block">
          {t('signup.letsSetupAccount')}
        </TypographyH2>
        <TypographyP className="text-text-primary lg:hidden">
          {t('signup.getStartedInMinutes')}
        </TypographyP>
        <TypographyP className="hidden text-text-primary lg:block">
          {t('signup.tailorHeidiToYou')}
        </TypographyP>
      </div>

      {/* Form fields - stacked on mobile, side-by-side on larger screens */}
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6">{nameFields}</div>

      {/* Continue button - pushed to bottom */}
      <div className="flex flex-1 items-end pt-4">
        <Button size="lg" type="submit" disabled={!isValid} className="w-full">
          {t('common.continue')}
        </Button>
      </div>
    </form>
  );
}
